import styled, { keyframes } from 'styled-components';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

export const Container = styled.div`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: linear-gradient(135deg, #1a1625 0%, #0f0d1a 50%, #1a1625 100%);
  color: #ffffff;
  line-height: 1.6;
  overflow-x: hidden;
  width: 100%;
  min-height: 100vh;
`;

export const AccessDeniedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 40px;
  text-align: center;
`;

export const AccessDeniedIcon = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  border: 3px solid rgba(239, 68, 68, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 32px;
  
  svg {
    font-size: 60px;
    color: #ef4444;
  }
`;

export const AccessDeniedTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const AccessDeniedText = styled.p`
  font-size: 1.125rem;
  color: #d1d5db;
  margin-bottom: 24px;
  max-width: 500px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

export const AccessDeniedEmail = styled.div`
  font-size: 0.875rem;
  color: #9ca3af;
  background: rgba(255, 255, 255, 0.05);
  padding: 12px 24px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

export const Navbar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 9999;
  background: linear-gradient(135deg, rgba(26, 22, 37, 0.95) 0%, rgba(15, 13, 26, 0.9) 100%);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(121, 173, 89, 0.15);
  box-shadow: 0 4px 24px rgba(121, 173, 89, 0.1);
`;

export const NavbarContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
`;

export const NavbarBrand = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  text-decoration: none;
  color: white;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.5px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

export const NavbarBrandIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(13, 15, 20, 1);
  border: 2px solid #79ad59;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  color: #79ad59;
  box-shadow: 0 4px 16px rgba(121, 173, 89, 0.2);
  transition: all 0.3s ease;

  ${NavbarBrand}:hover & {
    transform: scale(1.05);
    box-shadow: 0 6px 24px rgba(121, 173, 89, 0.3);
  }
`;

export const NavbarBrandText = styled.span`
  color: white;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.5px;
`;

export const NavbarToggle = styled.div`
  display: none;
  cursor: pointer;
  padding: 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 44px;
  height: 44px;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  span {
    width: 20px;
    height: 2px;
    background: #e5e7eb;
    position: absolute;
    left: 50%;
    top: 50%;
    transition: all 0.3s ease;
    border-radius: 1px;

    &:nth-child(1) {
      transform: translate(-50%, -50%) translateY(-6px);
      ${props => props.$active && 'transform: translate(-50%, -50%) rotate(45deg);'}
    }

    &:nth-child(2) {
      transform: translate(-50%, -50%);
      ${props => props.$active && 'opacity: 0;'}
    }

    &:nth-child(3) {
      transform: translate(-50%, -50%) translateY(6px);
      ${props => props.$active && 'transform: translate(-50%, -50%) rotate(-45deg);'}
    }
  }

  @media (max-width: 1330px) {
    display: flex;
  }
`;

export const NavbarNav = styled.ul`
  display: flex;
  align-items: center;
  gap: 32px;
  list-style: none;

  @media (max-width: 1330px) {
    position: fixed;
    top: 70px;
    right: ${props => props.$active ? '0' : '-100%'};
    width: 280px;
    height: calc(100vh - 70px);
    background: linear-gradient(135deg, rgba(17, 24, 39, 0.98) 0%, rgba(31, 41, 55, 0.95) 100%);
    backdrop-filter: blur(20px);
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    padding: 20px;
    gap: 0;
    box-shadow: -5px 0 20px rgba(0, 0, 0, 0.3);
    transition: right 0.3s ease;
    z-index: 9998;

    li {
      width: 100%;
    }
  }
`;

export const NavLink = styled.a`
  color: #d1d5db;
  text-decoration: none;
  font-weight: 500;
  font-size: 15px;
  padding: 12px 20px;
  border-radius: 12px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    bottom: 8px;
    left: 20px;
    right: 20px;
    height: 2px;
    background: linear-gradient(90deg, #79ad59, #5a8a42);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    color: #79ad59;
    background: rgba(121, 173, 89, 0.08);

    &::before {
      transform: scaleX(1);
    }
  }

  i {
    font-size: 14px;
    opacity: 0.8;
  }

  @media (max-width: 1330px) {
    width: 100%;
    padding: 16px 20px;
    font-size: 16px;
    border-radius: 0;
    justify-content: flex-start;

    &:hover {
      transform: translateX(4px);
    }
  }
`;

export const NavCTA = styled.a`
  background: linear-gradient(135deg, #79ad59 0%, #5a8a42 100%);
  color: white !important;
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(121, 173, 89, 0.3);
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(121, 173, 89, 0.3);

    &::before {
      left: 100%;
    }
  }

  @media (max-width: 1330px) {
    margin-top: 16px;
    border-radius: 12px;
  }
`;

export const NavbarOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.$active ? '1' : '0'};
  visibility: ${props => props.$active ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  z-index: 999;
`;

export const HeroSection = styled.section`
  padding: 120px 0 80px;
  background: linear-gradient(135deg, #1a1625 0%, #0f0d1a 50%, #1a1625 100%);
  position: relative;
  overflow: hidden;
  min-height: 100vh;
  display: flex;
  align-items: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 20%, rgba(121, 173, 89, 0.06) 0%, transparent 60%),
                radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.08) 0%, transparent 60%);
    pointer-events: none;
  }
`;

export const HeroContainer = styled.div`
  max-width: 55%;
  margin: 0 auto;
  padding: 0 40px;
  position: relative;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0 20px;
  }
`;

export const HeroContent = styled.div`
  max-width: 900px;
  text-align: left;
`;

export const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(121, 173, 89, 0.1);
  color: #79ad59;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid rgba(121, 173, 89, 0.3);
  margin-bottom: 24px;
`;

export const Pulse = styled.span`
  width: 8px;
  height: 8px;
  background: #79ad59;
  border-radius: 50%;
  animation: ${pulse} 2s infinite;
`;

export const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 24px;
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

export const Highlight = styled.span`
  background: linear-gradient(135deg, #79ad59 0%, #5a8a42 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  color: #d1d5db;
  margin-bottom: 32px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

export const ClientInfoCard = styled.div`
  margin-top: 48px;
  background: rgba(31, 41, 55, 0.8);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(121, 173, 89, 0.2);
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

export const ClientInfoHeader = styled.div`
  background: linear-gradient(90deg, #79ad59 0%, #5a8a42 100%);
  padding: 3px;
`;

export const ClientInfoContent = styled.div`
  padding: 32px;

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

export const ClientInfoTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 24px;
`;

export const ClientInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const InfoLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const InfoValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  background: rgba(121, 173, 89, 0.1);
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid rgba(121, 173, 89, 0.2);
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    color: #79ad59;
    font-size: 14px;
  }
`;

export const Section = styled.section`
  padding: 80px 0;
  position: relative;
  background: ${props => {
    if (props.$variant === '1') return 'linear-gradient(135deg, rgba(15, 13, 26, 1) 0%, rgba(26, 22, 37, 0.98) 100%)';
    if (props.$variant === '2') return 'linear-gradient(135deg, rgba(26, 22, 37, 1) 0%, rgba(15, 13, 26, 0.98) 100%)';
    if (props.$variant === '3') return 'linear-gradient(135deg, rgba(15, 13, 26, 1) 0%, rgba(26, 22, 37, 0.98) 100%)';
    if (props.$variant === '4') return 'linear-gradient(135deg, rgba(15, 13, 26, 1) 0%, rgba(26, 22, 37, 0.98) 100%)';
    if (props.$variant === '5') return 'linear-gradient(135deg, rgba(26, 22, 37, 1) 0%, rgba(15, 13, 26, 0.98) 100%)';
    return 'transparent';
  }};
`;

export const ContentWrapper = styled.div`
  max-width: 55%;
  margin: 0 auto;
  padding: 0 40px;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0 20px;
  }
`;

export const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 64px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -32px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 2px;
    background: linear-gradient(90deg, transparent, #79ad59, transparent);
  }
`;

export const SectionBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border-radius: 50px;
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;
  background: rgba(121, 173, 89, 0.08);
  border: 1px solid rgba(121, 173, 89, 0.2);
  color: #79ad59;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(121, 173, 89, 0.12);
    border-color: rgba(121, 173, 89, 0.3);
  }

  i {
    font-size: 12px;
    opacity: 0.8;
  }
`;

export const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  i {
    color: #79ad59;
    font-size: 1.75rem;
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    flex-direction: column;
    gap: 8px;
  }
`;

export const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: #9ca3af;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.7;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

export const HighlightsBox = styled.div`
  background: rgba(31, 41, 55, 0.8);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(121, 173, 89, 0.2);
  padding: 40px;
  position: relative;
  overflow: hidden;
  margin-bottom: 48px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, rgba(121, 173, 89, 0.4), transparent);
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

export const HighlightsTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;

  i {
    color: #79ad59;
  }
`;

export const HighlightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const HighlightItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(121, 173, 89, 0.2);
    transform: translateX(4px);
  }
`;

export const HighlightIcon = styled.div`
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 12px;
  background: rgba(121, 173, 89, 0.1);
  border: 1px solid rgba(121, 173, 89, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #79ad59;
  font-size: 14px;
`;

export const HighlightText = styled.div`
  flex: 1;
  font-size: 15px;
  color: #d1d5db;
  line-height: 1.5;

  strong {
    color: white;
    font-weight: 600;
  }
`;

export const SummaryCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 48px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const SummaryCard = styled.div`
  background: rgba(31, 41, 55, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 32px 24px;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #79ad59, #5a8a42);
  }

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(121, 173, 89, 0.3);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }
`;

export const SummaryCardIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  border-radius: 12px;
  background: rgba(121, 173, 89, 0.1);
  border: 1px solid rgba(121, 173, 89, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  ${SummaryCard}:hover & {
    transform: scale(1.05);
    background: rgba(121, 173, 89, 0.15);
  }

  i {
    font-size: 28px;
    color: #79ad59;
  }
`;

export const SummaryCardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #d1d5db;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

export const SummaryCardValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #79ad59;
  margin-bottom: 8px;
  line-height: 1;
`;

export const SummaryCardDescription = styled.p`
  font-size: 13px;
  color: #9ca3af;
  line-height: 1.5;
  margin: 0;
`;

// Blocos
export const BlocoCard = styled.div`
  background: rgba(31, 41, 55, 0.7);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 40px;
  margin-bottom: 32px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  }

  &:nth-of-type(1) {
    border-color: rgba(121, 173, 89, 0.2);

    &:hover {
      border-color: rgba(121, 173, 89, 0.4);
    }
  }

  &:nth-of-type(2) {
    border-color: rgba(16, 185, 129, 0.2);

    &:hover {
      border-color: rgba(16, 185, 129, 0.4);
    }
  }

  &:nth-of-type(3) {
    border-color: rgba(52, 211, 153, 0.2);

    &:hover {
      border-color: rgba(52, 211, 153, 0.4);
    }
  }

  &:nth-of-type(4) {
    border-color: rgba(34, 197, 94, 0.2);

    &:hover {
      border-color: rgba(34, 197, 94, 0.4);
    }
  }

  &:nth-of-type(5) {
    border-color: rgba(132, 204, 22, 0.2);

    &:hover {
      border-color: rgba(132, 204, 22, 0.4);
    }
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

export const BlocoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const BlocoNumber = styled.div`
  width: 72px;
  height: 72px;
  min-width: 72px;
  border-radius: 16px;
  border: 2px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  transition: all 0.3s ease;
  color: white;

  ${BlocoCard}:nth-of-type(1) & {
    background: rgba(121, 173, 89, 0.15);
    border-color: rgba(121, 173, 89, 0.4);
  }

  ${BlocoCard}:nth-of-type(2) & {
    background: rgba(16, 185, 129, 0.15);
    border-color: rgba(16, 185, 129, 0.4);
  }

  ${BlocoCard}:nth-of-type(3) & {
    background: rgba(52, 211, 153, 0.15);
    border-color: rgba(52, 211, 153, 0.4);
  }

  ${BlocoCard}:nth-of-type(4) & {
    background: rgba(34, 197, 94, 0.15);
    border-color: rgba(34, 197, 94, 0.4);
  }

  ${BlocoCard}:nth-of-type(5) & {
    background: rgba(132, 204, 22, 0.15);
    border-color: rgba(132, 204, 22, 0.4);
  }

  ${BlocoCard}:hover & {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    font-size: 24px;
  }
`;

export const BlocoInfo = styled.div`
  flex: 1;
`;

export const BlocoTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 700;
  color: white;
  margin-bottom: 8px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const BlocoMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
`;

export const BlocoMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #d1d5db;

  i {
    color: #79ad59;
    font-size: 16px;
  }

  strong {
    color: #79ad59;
    font-weight: 600;
  }
`;

export const BlocoInvestimento = styled.div`
  background: rgba(121, 173, 89, 0.1);
  border: 1px solid rgba(121, 173, 89, 0.2);
  padding: 12px 20px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #d1d5db;

  i {
    font-size: 18px;
    color: #79ad59;
  }

  strong {
    color: #79ad59;
  }
`;

export const BlocoContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

export const BlocoSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  padding: 24px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

export const BlocoSectionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #79ad59;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    font-size: 18px;
  }
`;

export const BlocoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const BlocoListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  font-size: 14px;
  color: #d1d5db;
  line-height: 1.6;

  i {
    color: #79ad59;
    font-size: 12px;
    margin-top: 3px;
    min-width: 12px;
  }
`;

// Timeline
export const TimelineHorizontal = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 64px;
  position: relative;
  padding: 0 40px;

  &::before {
    content: '';
    position: absolute;
    left: 110px;
    right: 110px;
    top: 50px;
    height: 2px;
    background: linear-gradient(90deg, #79ad59, #5a8a42);
    z-index: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0;
    gap: 24px;
    align-items: stretch;

    &::before {
      display: none;
    }
  }
`;

export const TimelineStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  position: relative;
  z-index: 1;
  flex: 1;
  max-width: 220px;

  @media (max-width: 768px) {
    max-width: 100%;
    flex-direction: row;
    align-items: flex-start;
    gap: 16px;
    background: rgba(31, 41, 55, 0.6);
    backdrop-filter: blur(20px);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 20px;
    transition: all 0.3s ease;

    &:hover {
      border-color: rgba(121, 173, 89, 0.3);
      transform: translateX(4px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    }
  }
`;

export const TimelineStepMarker = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 2px solid;
  backdrop-filter: blur(20px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  position: relative;

  ${TimelineStep}:nth-child(1) & {
    background: rgba(59, 130, 246, 0.15);
    border-color: rgba(59, 130, 246, 0.4);
  }

  ${TimelineStep}:nth-child(2) & {
    background: rgba(6, 182, 212, 0.15);
    border-color: rgba(6, 182, 212, 0.4);
  }

  ${TimelineStep}:nth-child(3) & {
    background: rgba(16, 185, 129, 0.15);
    border-color: rgba(16, 185, 129, 0.4);
  }

  ${TimelineStep}:nth-child(4) & {
    background: rgba(34, 197, 94, 0.15);
    border-color: rgba(34, 197, 94, 0.4);
  }

  ${TimelineStep}:nth-child(5) & {
    background: rgba(121, 173, 89, 0.15);
    border-color: rgba(121, 173, 89, 0.4);
  }

  &:hover {
    transform: translateY(-4px) scale(1.05);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  }

  @media (max-width: 768px) {
    width: 64px;
    height: 64px;
    min-width: 64px;
    flex-shrink: 0;
  }
`;

export const TimelineStepIcon = styled.i`
  font-size: 32px;
  margin-bottom: 2px;
  transition: all 0.3s ease;
  color: white;

  ${TimelineStepMarker}:hover & {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

export const TimelineStepNumber = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 1.5px;

  @media (max-width: 768px) {
    font-size: 9px;
    letter-spacing: 1px;
  }
`;

export const TimelineStepContent = styled.div`
  text-align: center;

  @media (max-width: 768px) {
    text-align: left;
    flex: 1;
  }
`;

export const TimelineStepTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: white;
  margin-bottom: 8px;
  line-height: 1.3;
  letter-spacing: -0.3px;
`;

export const TimelineStepDuration = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
  margin-bottom: 8px;

  i {
    font-size: 10px;
    opacity: 0.7;
  }

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

export const TimelineStepDescription = styled.div`
  font-size: 13px;
  color: #9ca3af;
  line-height: 1.6;
  opacity: 0.85;
`;

export const CronogramaSummary = styled.div`
  background: rgba(31, 41, 55, 0.7);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 40px;
  text-align: center;
  margin-top: 56px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(121, 173, 89, 0.5), transparent);
  }

  &:hover {
    border-color: rgba(121, 173, 89, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    padding: 24px;
    margin-bottom: 32px;
  }
`;

export const CronogramaSummaryTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #d1d5db;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  i {
    font-size: 14px;
    color: #79ad59;
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

export const CronogramaSummaryHighlight = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 16px;
  letter-spacing: -1px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  i {
    font-size: 2rem;
    color: #79ad59;
    opacity: 0.9;
  }

  span {
    color: #79ad59;
  }
`;

export const CronogramaSummaryText = styled.div`
  font-size: 1rem;
  color: #d1d5db;
  line-height: 1.6;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  i {
    font-size: 16px;
    color: #79ad59;
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

// Investimento
export const InvestimentoHighlight = styled.div`
  background: rgba(31, 41, 55, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 48px;
  text-align: center;
  margin-bottom: 48px;

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

export const InvestimentoLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #79ad59;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 16px;
`;

export const InvestimentoValor = styled.div`
  font-size: 3.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 16px;
  line-height: 1.1;
  letter-spacing: -1px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

export const InvestimentoDescricao = styled.div`
  font-size: 1.125rem;
  color: #d1d5db;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

export const InvestimentoDetalhes = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: center;
  }
`;

export const InvestimentoDetalheItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: #d1d5db;

  i {
    color: #79ad59;
    font-size: 18px;
  }

  strong {
    color: white;
  }
`;

export const ModalidadesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-bottom: 48px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

export const ModalidadeCard = styled.div`
  background: rgba(31, 41, 55, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 36px;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);

  &:hover {
    border-color: rgba(255, 255, 255, 0.12);
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

export const ModalidadeIcon = styled.div`
  width: 72px;
  height: 72px;
  margin: 0 auto 24px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  ${ModalidadeCard}:hover & {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.12);
  }

  i {
    font-size: 32px;
    color: #d1d5db;
    transition: all 0.3s ease;
  }

  ${ModalidadeCard}:nth-child(1) & i {
    color: rgba(167, 139, 250, 0.7);
  }

  ${ModalidadeCard}:nth-child(1):hover & i {
    color: rgba(167, 139, 250, 0.9);
  }

  ${ModalidadeCard}:nth-child(2) & i {
    color: rgba(96, 165, 250, 0.7);
  }

  ${ModalidadeCard}:nth-child(2):hover & i {
    color: rgba(96, 165, 250, 0.9);
  }

  ${ModalidadeCard}:nth-child(3) & i {
    color: rgba(251, 191, 36, 0.7);
  }

  ${ModalidadeCard}:nth-child(3):hover & i {
    color: rgba(251, 191, 36, 0.9);
  }

  @media (max-width: 768px) {
    width: 64px;
    height: 64px;

    i {
      font-size: 28px;
    }
  }
`;

export const ModalidadeTitulo = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

export const ModalidadeValor = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

export const ModalidadeDescricao = styled.p`
  font-size: 14px;
  color: #d1d5db;
  line-height: 1.6;
  margin-bottom: 20px;
`;

export const ModalidadeDetalhes = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const ModalidadeDetalheItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 0;
  font-size: 13px;
  color: #d1d5db;

  i {
    color: #79ad59;
    font-size: 12px;
  }
`;

// Contato
export const ContatoInfoBox = styled.div`
  background: rgba(31, 41, 55, 0.6);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 48px;
  text-align: center;
  margin-bottom: 48px;

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

export const ContatoInfoTitulo = styled.h3`
  font-size: 1.75rem;
  font-weight: 700;
  color: white;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const ContatoInfoSubtitulo = styled.p`
  font-size: 1rem;
  color: #d1d5db;
  margin-bottom: 40px;
`;

export const ContatoDetalhes = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

export const ContatoItem = styled.div`
  text-align: center;
`;

export const ContatoItemLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #d1d5db;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
`;

export const ContatoItemValor = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  i {
    font-size: 16px;
    color: #79ad59;
  }
`;

export const ContatoEndereco = styled.div`
  padding-top: 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

export const ContatoEnderecoTitulo = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #d1d5db;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

export const ContatoEnderecoTexto = styled.p`
  font-size: 14px;
  color: #d1d5db;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;
